import { locale } from '../lib/locale';

/**
 * „Einfach sagen“ (1.19, glossary: “Just say it”, KI-Helfer = AI helper): input, suggestions,
 * the teaser while the AI helper is off. German is the master.
 */
export const MAGIC_TEXT = {
  de: {
    kinds: {
      event: 'Termin',
      todo: 'Aufgabe',
      shopping: 'Einkauf',
      note: 'Pinnwand',
      timetable: 'Stundenplan',
      task: 'Aufgabe',
      edit: 'Ändern',
      idea: 'Ausflugsidee',
      voucher: 'Gutschein',
      wish: 'Wunsch',
    },
    everyone: 'Alle',
    until: (day: string) => `bis ${day}`,
    clock: (time: string) => `${time} Uhr`,
    allDay: 'ganztägig',
    bring: (items: string) => `Mitnehmen: ${items}`,
    toBring: 'auf die Bring!-Liste',
    toList: 'auf die Einkaufsliste',
    deleted: 'wird gelöscht',
    inFun: (place: string) => `${place} · in Freizeit`,
    code: (code: string) => `Code ${code}`,
    from: (from: string) => `von ${from}`,
    wishesOf: (name: string) => `Wunschzettel von ${name}`,
    familyWish: 'Familienwunsch',
    school: 'Schule',
    timetableDays: (label: string, n: string) => `${label}: ${n} Tage`,
    notFoundShort: 'Nicht gefunden',
    whoseTimetable: 'Für wen ist der Stundenplan?',
    tasksParents: 'Aufgaben mit Sternen legen Mama oder Papa an.',
    ownWishes: 'Wünsche trägt jeder auf seinem eigenen Wunschzettel ein.',
    notFound: 'Nicht gefunden – bitte von Hand ändern.',
    badPhoto: 'Dieses Foto kann ich nicht öffnen. Bitte ein anderes wählen.',
    oneFailed: 'Ein Vorschlag konnte nicht gespeichert werden.',
    someFailed: (n: string) => `${n} Vorschläge konnten nicht gespeichert werden.`,
    savedOne: 'Eingetragen!',
    savedMany: (n: string) => `${n} Dinge eingetragen!`,
    nothingFound: 'Dazu habe ich nichts zum Eintragen gefunden. Probier es anders.',
    check: 'Das habe ich verstanden – bitte kurz prüfen. Eingetragen wird nur, was angehakt ist.',
    suggestions: 'Vorschläge',
    sayMore: 'Noch etwas sagen',
    saving: 'Trage ein …',
    take: (n: string) => `Übernehmen (${n})`,
    done: 'Fertig',
    what: 'Was soll eingetragen werden?',
    example:
      'z. B. Lotta hat nächsten Dienstag um 15 Uhr Zahnarzt, Impfpass mitnehmen. Und wir brauchen Milch.',
    listening: 'Ich höre zu … (stopp)',
    speak: 'Sprechen',
    stop: 'Stopp',
    micStarting: 'Mikrofon startet …',
    speakNow: 'Jetzt sprechen – tippe auf „Fertig“, wenn du fertig bist.',
    micBlocked:
      'Das Mikrofon ist gesperrt. Erlaube es in den Einstellungen deines Browsers und versuche es noch einmal.',
    camera: 'Foto aufnehmen',
    otherPhoto: 'Anderes Foto',
    pickPhoto: 'Foto auswählen',
    photoAlt: 'Gewähltes Foto',
    removePhoto: 'Foto entfernen',
    youCanSay: 'Das kannst du sagen',
    examples: [
      'Termin: „Lotta hat Dienstag um 15 Uhr Zahnarzt, Impfpass mitnehmen“',
      'Ändern: „Fußball ist heute erst um 18 Uhr“ · Löschen: „Lösch den Zahnarzt am Dienstag“',
      'Aufgabe mit Sternen: „Jonas bringt samstags den Müll raus, 5 Sterne“',
      'Aufgabe: „Geschenk für Oma besorgen“ · Einkauf: „Wir brauchen Milch und Eier“',
      'Notiz: „Notiz für alle: Schlüssel liegt beim Nachbarn“',
      'Foto: Papierkalender, Stundenplan („Stundenplan von Lotta“), Einladung, Schulzettel',
    ],
    cancel: 'Abbrechen',
    reading: 'Die KI liest mit …',
    ask: 'Vorschläge holen',
    delete: 'Löschen',
    inCalendar: 'Steht schon im Kalender',
    otherTime: (time: string) => `Im Kalender um ${time} Uhr – auf dem Foto eine andere Zeit`,
    change: 'Ändern',
    whichOne: 'Welcher ist gemeint?',
    forWhom: 'Für wen?',
    replaces: 'Ersetzt den bisherigen Stundenplan.',
    teaserTitle: 'Sag es einfach – die App trägt es ein.',
    teaser: [
      '„Lotta hat Dienstag um 15 Uhr Zahnarzt, Impfpass mitnehmen“ wird ein fertiger Termin.',
      'Papierkalender oder Stundenplan abfotografieren – alles auf einmal eingetragen.',
      '„Wir brauchen Milch und Eier“ landet direkt auf der Einkaufsliste.',
    ],
    switchOn: 'KI-Helfer in den Einstellungen einschalten',
    parentsSwitchOn: 'Mama oder Papa können den KI-Helfer in den Einstellungen einschalten.',
    dayparts: {
      morning: 'morgens',
      afternoon: 'nachmittags',
      evening: 'abends',
      anytime: 'irgendwann am Tag',
    },
    once: 'einmal',
    everyDay: 'jeden Tag',
    forAll: 'für alle',
    stars: (n: number) => (n === 1 ? '1 Stern' : `${n} Sterne`),
    wholeSeries: 'ganze Serie',
    newName: (title: string) => `neuer Name: ${title}`,
    onDay: (day: string) => `auf ${day}`,
    forWho: (names: string) => `für ${names}`,
  },
  en: {
    kinds: {
      event: 'Event',
      todo: 'Task',
      shopping: 'Shopping',
      note: 'Pinboard',
      timetable: 'Timetable',
      task: 'Task',
      edit: 'Change',
      idea: 'Day-out idea',
      voucher: 'Voucher',
      wish: 'Wish',
    },
    everyone: 'Everyone',
    until: (day: string) => `until ${day}`,
    clock: (time: string) => time,
    allDay: 'all day',
    bring: (items: string) => `Bring: ${items}`,
    toBring: 'onto the Bring! list',
    toList: 'onto the shopping list',
    deleted: 'will be deleted',
    inFun: (place: string) => `${place} · in Fun`,
    code: (code: string) => `Code ${code}`,
    from: (from: string) => `from ${from}`,
    wishesOf: (name: string) => `${name}’s wish list`,
    familyWish: 'Family wish',
    school: 'School',
    timetableDays: (label: string, n: string) => `${label}: ${n} days`,
    notFoundShort: 'Not found',
    whoseTimetable: 'Whose timetable is it?',
    tasksParents: 'Mum or Dad add tasks with stars.',
    ownWishes: 'Everyone adds wishes to their own wish list.',
    notFound: 'Not found – please change it by hand.',
    badPhoto: 'I can’t open this photo. Please choose another one.',
    oneFailed: 'One suggestion couldn’t be saved.',
    someFailed: (n: string) => `${n} suggestions couldn’t be saved.`,
    savedOne: 'Added!',
    savedMany: (n: string) => `${n} things added!`,
    nothingFound: 'I didn’t find anything to add. Try saying it differently.',
    check: 'This is what I understood – please check. Only what’s ticked gets added.',
    suggestions: 'Suggestions',
    sayMore: 'Say something else',
    saving: 'Adding …',
    take: (n: string) => `Add (${n})`,
    done: 'Done',
    what: 'What should be added?',
    example:
      'e.g. Lotta has the dentist next Tuesday at 3 pm, bring the vaccination card. And we need milk.',
    listening: 'Listening … (stop)',
    speak: 'Speak',
    stop: 'Stop',
    micStarting: 'Starting the microphone …',
    speakNow: 'Speak now – tap “Done” when you have finished.',
    micBlocked: 'The microphone is blocked. Allow it in your browser’s settings and try again.',
    camera: 'Take a photo',
    otherPhoto: 'Another photo',
    pickPhoto: 'Choose a photo',
    photoAlt: 'Chosen photo',
    removePhoto: 'Remove photo',
    youCanSay: 'Things you can say',
    examples: [
      'Event: “Lotta has the dentist on Tuesday at 3 pm, bring the vaccination card”',
      'Change: “Football is at 6 pm today” · Delete: “Delete the dentist on Tuesday”',
      'Task with stars: “Jonas takes the bins out on Saturdays, 5 stars”',
      'Task: “Get a present for Grandma” · Shopping: “We need milk and eggs”',
      'Note: “Note for everyone: the key is with the neighbours”',
      'Photo: paper calendar, timetable (“Lotta’s timetable”), invitation, school letter',
    ],
    cancel: 'Cancel',
    reading: 'The AI is reading …',
    ask: 'Get suggestions',
    delete: 'Delete',
    inCalendar: 'Already in the calendar',
    otherTime: (time: string) => `In the calendar at ${time} – a different time on the photo`,
    change: 'Change',
    whichOne: 'Which one do you mean?',
    forWhom: 'For whom?',
    replaces: 'Replaces the current timetable.',
    teaserTitle: 'Just say it – the app adds it.',
    teaser: [
      '“Lotta has the dentist on Tuesday at 3 pm, bring the vaccination card” becomes a finished event.',
      'Take a photo of a paper calendar or timetable – everything added at once.',
      '“We need milk and eggs” goes straight onto the shopping list.',
    ],
    switchOn: 'Turn on the AI helper in Settings',
    parentsSwitchOn: 'Mum or Dad can turn on the AI helper in Settings.',
    dayparts: {
      morning: 'in the morning',
      afternoon: 'in the afternoon',
      evening: 'in the evening',
      anytime: 'any time of day',
    },
    once: 'once',
    everyDay: 'every day',
    forAll: 'for everyone',
    stars: (n: number) => (n === 1 ? '1 star' : `${n} stars`),
    wholeSeries: 'whole series',
    newName: (title: string) => `new name: ${title}`,
    onDay: (day: string) => `to ${day}`,
    forWho: (names: string) => `for ${names}`,
  },
} as const;

export const magicText = MAGIC_TEXT[locale];
